import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { fetchDeviceCount, hospitalUserWaitingList } from '@utils/utils';
import { HOSPITAL, INDIVIDUAL, NURSE, ADMIN } from '@constants/constants';
import Swal from 'sweetalert2';
import {
    adminDeleteUser,
    fetchCountries,
    getUserInfo,
    getUserName,
    handleFetchLogo,
} from '@utils/utils';
import { setGoToDashboard, setHospitalDetail } from '@store/reducers/userSlice';
import {
    capitalizeFirstLetter,
    getDataUsingTenantId,
    getDataUsingUserId,
} from '@utils/sharedUtils';
import { useAppDispatch } from '@store/hooks';
const MySwal = Swal;
interface UserData {
    id: string;
    email?: string;
    name?: string;
    deviceId?: string;
    mobileNo?: string;
    createdAt?: string;
    createdAt_time?: string;
    hospitalName?: string;
    hospitalContact?: string;
    status?: string;
}
const PreAuthorizationList: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const hospitalData = [
        {
            id: 1,
            name: 'General Hospital',
            email: 'contact@generalhospital.com',
            contact: '976543290',
            status: 'Waiting',
        },
        {
            id: 2,
            name: 'City Hospital',
            email: 'info@cityhospital.com',
            contact: '976543221',
            status: 'Waiting',
        },
        {
            id: 3,
            name: 'Sri Lakshmi Health Care',
            email: 'Support.Lakshmisri@gmail.com',
            contact: '976543218',
            status: 'Waiting',
        },
        {
            id: 4,
            name: 'Ridgemont General Hospital',
            email: 'contactus@ridgemont.com',
            contact: '976543215',
            status: 'Waiting',
        },
        {
            id: 5,
            name: 'Hopewell Medical Center',
            email: 'contactus@hopewell.com',
            contact: '976543255',
            status: 'Waiting',
        },
        {
            id: 6,
            name: 'Sindhu Valley Wellness Centre',
            email: 'sindhuvalley@gmail.com',
            contact: '555-555-5555',
            status: 'Waiting',
        },
    ];

    const totalPages = Math.ceil(hospitalData.length / rowsPerPage);

    const handlePageChange = (page: number) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
        }
    };
    const [hospitalUserData, setHospitalUserData] = useState<UserData[]>([]);
    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await hospitalUserWaitingList(HOSPITAL);
                setHospitalUserData(data);
            } catch (error: any) {
                let errorMessage = 'Unexpected error';

                switch (error.name) {
                    case 'ResourceNotFoundException':
                        errorMessage = 'Table or item not found.';
                        break;
                    case 'TooManyRequestsException':
                        errorMessage =
                            'Too many requests. Please try again later.';
                        break;
                    case 'ProvisionedThroughputExceededException':
                        errorMessage =
                            'Request limit exceeded. Please try again later.';
                        break;
                    case 'ServiceException':
                        errorMessage = `Service error: ${error.message}`;
                        break;
                    default:
                        errorMessage = `Unexpected error: ${error.message}`;
                        break;
                }
                MySwal.fire({
                    position: 'top-end', // Position the toast in the top-right corner
                    icon: 'error',
                    title: errorMessage,
                    showConfirmButton: false,
                    timer: 3000,
                    toast: true,
                    customClass: {
                        popup: '!text-red !bg-light_red', // Add custom class to the popup
                    },
                    width: 'auto',
                });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);
    if (hospitalUserData.length === 0) {
        return (
            <div className="card rounded-2xl w-full mx-auto p-0 !mt-5">
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="border-top m-3">
                            <tr>
                                <th className="!border-t-0 border font-bold !normal-case p-3">
                                Hospital Name
                                </th>
                                <th className="!border-t-0 border font-bold p-3 !normal-case">
                                    Hospital Email ID
                                </th>
                                <th className="!border-t-0 border font-bold p-3 !normal-case">
                                    Hospital Contact No
                                </th>
                                <th className="!border-t-0 border font-bold p-3 !normal-case">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="text-center">
                                <td colSpan={hospitalUserData.length + 4}>
                                    {t('user_management.user_table.no_data')}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
    const currentData = hospitalUserData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const disablePrevious = currentPage === 1;
    const disableNext = currentPage === totalPages;



    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
        handlePageChange(1); // Reset to first page
    };

    const goToPreviousPage = () => {
        if (!disablePrevious) {
            handlePageChange(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (!disableNext) {
            handlePageChange(currentPage + 1);
        }
    };

    const handleRowClick = async (user: any) => {
        // Determine the path based on the user's role or any other attribute
        // const path = user.hospital_admin_status
        //     ? `/hospital/${user.id}`
        //     : `/individual-user/${user.id}`;
        // dispatch(setUserDeviceList(list));
        // if () {}
        if (user.role.toLowerCase() == HOSPITAL.toLowerCase()) {
            const hospitalRes = await getUserInfo(
                'hospital_info',
                user.tenantId
            );
            const userData = await getDataUsingUserId('user_info', user.id);
            const hosData = await getDataUsingTenantId(
                'hospitals',
                user.tenantId
            );

            // Extracting additional details from the response
            if (
                hospitalRes &&
                userData &&
                userData.Items &&
                userData.Items.length > 0 &&
                hosData &&
                hosData.Items &&
                hosData.Items.length > 0
            ) {
                const additionalDetails = hospitalRes[0];
                const details = userData.Items[0];
                const hosDetails = hosData.Items[0];

                const logo = await handleFetchLogo(
                    additionalDetails.hospital_logo?.S || ''
                );

                const mergedUserDetails = {
                    id: user.id,
                    tenantUUid: user.tenantId,
                    device_id: user.deviceId,
                    hospital_id: details.hospital_id?.S
                        ? details.hospital_id?.S
                        : '--',
                    hospital_name: additionalDetails.hospital_name?.S
                        ? additionalDetails.hospital_name?.S
                        : '--',
                    hospital_branch: additionalDetails.hospital_branch?.S
                        ? additionalDetails.hospital_branch?.S
                        : '--',
                    hospital_website: additionalDetails.hospital_website?.S
                        ? additionalDetails.hospital_website?.S
                        : '--',
                    gst_no: additionalDetails.gst?.S
                        ? additionalDetails.gst?.S
                        : '--',
                    contact_no: additionalDetails.hospital_contact?.S
                        ? additionalDetails.hospital_contact?.S
                        : '--',
                    email_id: additionalDetails.hospital_email?.S
                        ? additionalDetails.hospital_email?.S
                        : '--',
                    address:
                        additionalDetails && additionalDetails.address1
                            ? `${additionalDetails.address1?.S || ''}, ${additionalDetails.address2?.S || ''}, ${additionalDetails.city?.S || ''}, ${additionalDetails.state?.S || ''}, ${additionalDetails.country?.S || ''} - ${additionalDetails.pincode?.S || ''}`.trim()
                            : '--',
                    admin_name: user.name,
                    mobile_no: user.mobileNo,
                    admin_email: user.email,
                    create_date: user.createdAt,
                    hospital_admin_status: hosDetails?.status?.S
                        ? hosDetails?.status?.S
                        : '--',
                    // hospital_admin_status: capitalizeFirstLetter(
                    //     details?.status?.S ? details?.status?.S : '--'
                    // ),
                    logo: logo || null,
                    approvedOn: additionalDetails.approved_on?.S ? new Date(additionalDetails.approved_on.S).toLocaleDateString('en-GB') : '--',
                };
                dispatch(setGoToDashboard(true));
                dispatch(setHospitalDetail(mergedUserDetails));
                navigate(`/hospital-users/${details.slug?.S}`);
            }
        }
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 3;

        if (totalPages <= 6) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(renderPageButton(i));
            }
        } else {
            if (currentPage <= maxVisiblePages) {
                for (let i = 1; i <= maxVisiblePages + 1; i++) {
                    pageNumbers.push(renderPageButton(i));
                }
                pageNumbers.push(renderEllipsis());
                pageNumbers.push(renderPageButton(totalPages));
            } else if (currentPage >= totalPages - maxVisiblePages + 1) {
                pageNumbers.push(renderPageButton(1));
                pageNumbers.push(renderEllipsis());
                for (
                    let i = totalPages - maxVisiblePages + 1;
                    i <= totalPages;
                    i++
                ) {
                    pageNumbers.push(renderPageButton(i));
                }
            } else {
                pageNumbers.push(renderPageButton(1));
                pageNumbers.push(renderEllipsis());

                const startPage = currentPage - 1;
                const endPage = currentPage + 1;

                for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(renderPageButton(i));
                }

                pageNumbers.push(renderEllipsis());
                pageNumbers.push(renderPageButton(totalPages));
            }
        }

        return pageNumbers;
    };

    const renderPageButton = (pageNumber: number) => (
        <button
            key={pageNumber}
            className={`mx-1 pagination-padding py-2 border rounded ${pageNumber === currentPage ? '!bg-primary text-white' : 'bg-white text-black'}`}
            onClick={() => handlePageChange(pageNumber)}
        >
            {pageNumber}
        </button>
    );

    const renderEllipsis = () => (
        <span key="ellipsis" className="mx-1 py-2">
            ...
        </span>
    );

    return (
        // <div className="container w-[90%] bg-white flex flex-col ml-0 rounded-2xl relative mt-5 py-4">
        <div className="bg-white flex flex-col ml-0 rounded-2xl relative mt-5 p-0">
            <table className="datatables-users table">
                <thead>
                    <tr className="border-none">
                        <th className="!border-t-0 border font-bold !normal-case p-3">
                            {t('preAuth.hospital_name')}
                        </th>
                        <th className="!border-t-0 border font-bold p-3 !normal-case">
                            {t('preAuth.hospital_email_id')}
                        </th>
                        <th className="!border-t-0 border font-bold p-3 !normal-case">
                            {t('preAuth.hospital_contact_no')}
                        </th>
                        <th className="!border-t-0 border font-bold p-3 !normal-case">
                            {t('preAuth.status')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {currentData.map((hospital) => (
                        <tr
                            key={hospital.id}
                            onClick={() => handleRowClick(hospital)}
                            className="border cursor-pointer"
                        >
                            <td className="border">{hospital.hospitalName}</td>
                            <td className="border">{hospital.email}</td>
                            <td className="border">
                                {hospital.hospitalContact}
                            </td>
                            <span className="text-center">
                                <td
                                    className={`text-white ${hospital.status === 'Waiting' ? '!bg-status_waiting' : 'bg-status_other'} inline-block rounded-full px-2 py-1 my-1`}
                                >
                                    {hospital.status}
                                </td>
                            </span>
                        </tr>
                    ))}
                </tbody>
            </table>
            {/* <div className="pb-4 mt-5 flex justify-between items-center absolute -bottom-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="flex justify-center mx-auto">
                    <button
                        className="btn bg-white text-black text-sm p-1"
                        onClick={goToPreviousPage}
                        disabled={disablePrevious}
                    >
                        {t('user_management.pagination.previous')}
                    </button>
                    {renderPageNumbers()}
                    <button
                        className="btn bg-white text-black text-sm p-1"
                        onClick={goToNextPage}
                        disabled={disableNext}
                    >
                        {t('user_management.pagination.next')}
                    </button>
                </div>
            </div> */}
        </div>
    );
};
export default PreAuthorizationList;
